import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useParams } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import BrandedBootLoader from "Components/common/BrandedBootLoader";
import BreadCrumbComponent from "Components/common/breadcrumb";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { FREQUENCY_OPTIONS, daysOf, scheduleText } from "./DayOfWeekField";
import { icpToListBuilderImport, encodeListBuilderImport } from "./icpListBuilder";
import { dataSearchState } from "../dataSearchV2/state";
import TriggerFilters from "./TriggerFilters";
import { eventOptions, filterConfigFor, filterSummary } from "./triggerCatalog";


// IMPORTANT_ONLY is the old stored name for ON_FAILURE — an agent saved before the rename still has it.
const NOTIFY_LABEL = {
  DAILY_DIGEST: "Daily digest", EVERY_RUN: "After every run",
  ON_FAILURE: "Only when a run fails", IMPORTANT_ONLY: "Only when a run fails", OFF: "Off"
};
const NOTIFY_OPTIONS = [
  { value: "DAILY_DIGEST", label: "Daily digest" },
  { value: "EVERY_RUN", label: "After every run" },
  { value: "ON_FAILURE", label: "Only when a run fails" },
  { value: "OFF", label: "Off" }
];
const CHANNEL_LABEL = { email: "Email", slack: "Slack" };

const EVENT_SHORT = {
  EMAIL_REPLIED: "Email replied", LINKEDIN_REPLIED: "LinkedIn replied",
  LI_CONNECTION_REQUEST_ACCEPTED: "LinkedIn connection accepted", EMAIL_OPENED: "Email opened",
  EMAIL_CLICKED: "Link clicked", WEBSITE_VISITED: "Website visited", CONTACT_CREATED: "Contact created",
  CONTACT_UPDATED: "Contact updated", CONTACT_STAGE_CHANGED: "Stage changed",
  ADDED_TO_SEQUENCE: "Added to sequence", SEQUENCE_COMPLETED: "Sequence finished",
  COMPLETED_ON_ALL_SEQUENCES: "Finished sequences", CALL_LOGGED: "Call logged",
  CONTACT_OPTEDOUT: "Opted out", EMAIL_BOUNCED: "Email bounced", EMAIL_DELIVERED: "Email delivered"
};
const STATUS_LABEL = {
  SUCCEEDED: "Succeeded", FAILED: "Failed", PARTIAL: "Partial", CANCELLED: "Cancelled",
  RUNNING: "Running", QUEUED: "Queued", WAITING_APPROVAL: "Awaiting approval", SLEEPING: "Waiting"
};
const STEP_KIND_LABEL = {
  SOURCE: "Source", EXPAND: "Expand", ENRICH: "Enrich", FILTER: "Filter", BRANCH: "Branch",
  WAIT: "Wait", ACT: "Action", APPROVE: "Approval", NOTIFY: "Notify"
};
// Agent lifecycle status → header pill label.
const AGENT_STATUS_LABEL = { ACTIVE: "Live", PAUSED: "Paused", DRAFT: "Draft", ARCHIVED: "Archived", DELETED: "Deleted" };
const prettify = s => String(s || "").replace(/_/g, " ").toLowerCase().replace(/^./, c => c.toUpperCase());
const friendlyTrigger = src => {
  if (!src) return "—";
  if (src.startsWith("event.")) { const k = src.slice(6); return EVENT_SHORT[k] || prettify(k); }
  if (src === "schedule.recurring") return "Scheduled";
  if (src === "manual.test") return "Test run";
  if (src === "manual") return "Manual";
  return prettify(src);
};
const runPill = st => st === "SUCCEEDED" ? "ag-pill--live" : (st === "FAILED" || st === "CANCELLED") ? "ag-pill--warn" : "ag-pill--draft";

const triggerKindOf = agent => {
  const t = agent.trigger && agent.trigger.type;
  if (t === "schedule.recurring") return "schedule";
  if (t && t.startsWith("event.")) return "event";
  return "manual";
};

const initSettings = agent => {
  const tc = (agent.trigger && agent.trigger.config) || {};
  const cfg = agent.config || {};
  return {
    name: agent.name || "",
    frequency: tc.frequency || "DAILY",
    time: tc.time || "08:00",
    days: daysOf(tc),
    eventType: agent.trigger && agent.trigger.type && agent.trigger.type.startsWith("event.") ? agent.trigger.type.slice(6) : "EMAIL_REPLIED",
    eventConfig: tc,
    dailyCap: cfg.dailyRecordCap != null ? cfg.dailyRecordCap : 25,
    notifyMode: cfg.notifyMode === "IMPORTANT_ONLY" ? "ON_FAILURE" : (cfg.notifyMode || "DAILY_DIGEST"),
    // Nothing saved means email, which is what every agent did before Slack was an option.
    notifyChannels: (cfg.notifyChannels && cfg.notifyChannels.length) ? cfg.notifyChannels.slice() : ["email"],
    slackChannelId: cfg.slackChannelId || "",
    requireApproval: cfg.approvalMode === "REQUIRED"
  };
};

// "Daily digest · Email, Slack #pipeline" for the overview strip.
const notifySummary = (edit, agent) => {
  const mode = edit && edit.notifyMode;
  if (!mode) return "\u2014";
  const label = NOTIFY_LABEL[mode] || "\u2014";
  if (mode === "OFF") return label;
  const channels = (edit.notifyChannels || []).map(ch => {
    if (ch !== "slack") return CHANNEL_LABEL[ch] || ch;
    const cfg = (agent && agent.config) || {};
    return cfg.slackChannelName ? "Slack #" + cfg.slackChannelName : "Slack";
  });
  return channels.length ? label + " \u00b7 " + channels.join(", ") : label;
};

// Friendly, human-readable trigger summary for the overview strip.
const triggerSummary = agent => {
  const t = agent.trigger || {};
  if (t.type === "schedule.recurring" && t.config) {
    const s = scheduleText(t.config.frequency, t.config.time, daysOf(t.config));
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  if (t.type && t.type.startsWith("event.")) {
    const narrowed = filterSummary(t.type.slice("event.".length), t.config);
    return friendlyTrigger(t.type) + (narrowed ? ` — ${narrowed}` : "");
  }
  return "Manual";
};

const DISPOSITION = {
  ENROLLED: { label: "Enrolled", cls: "ag-disp--good" },
  PROCESSED: { label: "Processed", cls: "ag-disp--good" },
  SOURCED: { label: "Sourced", cls: "ag-disp--neutral" },
  AWAITING_APPROVAL: { label: "Awaiting approval", cls: "ag-disp--wait" },
  WAITING: { label: "Waiting", cls: "ag-disp--wait" },
  REJECTED: { label: "Not approved", cls: "ag-disp--skip" },
  SKIPPED: { label: "Skipped", cls: "ag-disp--skip" }
};

const AgentDetail = observer(() => {
  const { id } = useParams();
  const history = useHistory();
  const [filters, setFilters] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [edit, setEdit] = useState(null);
  const [savedTick, setSavedTick] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stepDefs, setStepDefs] = useState([]);
  const [askRun, setAskRun] = useState(null); // { fields:[{key,label,type,options}], values:{} }

  const toggleRun = runId => {
    if (expandedRun === runId) { setExpandedRun(null); return; }
    setExpandedRun(runId);
    if (!agentsData.runRecords[runId]) agentsData.fetchRunRecords(id, runId);
  };

  useEffect(() => {
    setLoaded(false);
    setNotFound(false);
    agentsData.fetchTriggerTypes(); // event labels + which filters each event offers
    agentsData.fetchSequencesLite(); // so a sequence filter shows its name, not its id
    agentsData.fetchSlackChannels(); // empty list = Slack isn't connected, so we can say so
    (async () => {
      const agent = await agentsData.getAgent(id);
      if (!agent) {
        setNotFound(true);
        setLoaded(true);
        return;
      }
      setEdit(initSettings(agent));
      agentsData.fetchStepTypes().then(setStepDefs); // for "ask each run" prompts
      await agentsData.fetchRuns(id);
      const icp = agent && (agent.templateKey === "daily_icp_prospector" || (agent.config && agent.config.sellerProfileId));
      if (icp) {
        agentsData.fetchSavedSearches(); // the ICP is one of the member's List Builder saved searches
        const preview = await agentsData.previewIcp(id);
        setFilters((preview && preview.filters) || {});
      }
      setLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const agent = agentsData.currentAgent;
  const { runs, busy, testing } = agentsData;
  if (notFound) {
    return (
      <div className="ag-page">
        <div className="ag-empty">
          <div className="ag-empty__title">Agent not found</div>
          <div className="ag-empty__actions">
            <button className="ag-btn ag-btn--primary" onClick={() => history.push("/agents")}>Back to Agents</button>
          </div>
        </div>
      </div>
    );
  }
  if (!loaded || !agent) return <div className="ag-page"><BrandedBootLoader /></div>;

  const isIcp = agent.templateKey === "daily_icp_prospector" || (agent.config && agent.config.sellerProfileId);
  // Runs of agents that don't enroll (export, tasks, tags…) finish contacts as "Processed", not "Enrolled".
  const enrolls = (agent.flow || []).some(n => n.stepType === "act.enroll_sequence");
  const doneLabel = enrolls ? "Enrolled" : "Processed";
  const approved = isIcp ? (agent.config && agent.config.icpApproved) : true;
  const kind = triggerKindOf(agent);

  // Friendly step name from the registry (falls back to the raw type).
  const stepName = type => {
    const d = (stepDefs || []).find(x => x.type === type);
    return (d && d.displayName) || type;
  };
  // The step's kind label ("Source", "Action"…); before step defs load, read it off the type prefix.
  const stepKindLabel = type => {
    const d = (stepDefs || []).find(x => x.type === type);
    const kind = d && d.kind ? d.kind : String(type || "").split(".")[0].toUpperCase();
    return STEP_KIND_LABEL[kind] || prettify(kind);
  };

  // Dry run → refresh history and open the new run so its plan (the per-step notes) is visible.
  const onTestRun = async () => {
    const run = await agentsData.testRun(id);
    await agentsData.fetchRuns(id);
    if (run && run.id) setExpandedRun(run.id);
  };

  // "Ask each run" inputs: params stored as "{{ask}}" on the flow, resolved to labels via step defs.
  const askFields = () => {
    const byType = {};
    (stepDefs || []).forEach(d => { byType[d.type] = d; });
    const seen = {};
    const out = [];
    (agent.flow || []).forEach(n => {
      const params = (byType[n.stepType] && byType[n.stepType].params) || [];
      params.forEach(p => {
        if (n.params && n.params[p.key] === "{{ask}}" && !seen[p.key]) {
          seen[p.key] = true;
          out.push({ key: p.key, label: p.label, type: p.type, options: p.options });
        }
      });
      // A post import asked for a new URL each run also asks who to import for that post —
      // reactions, comments, or both — starting from the agent's saved choice.
      if (n.stepType === "source.from_linkedin_post" && n.params && n.params.url === "{{ask}}" && !seen.engagementType) {
        const p = params.find(x => x.key === "engagementType");
        if (p) {
          seen.engagementType = true;
          const saved = n.params.engagementType && n.params.engagementType !== "{{ask}}" ? n.params.engagementType : "all";
          out.push({ key: p.key, label: p.label, type: p.type, options: p.options, defaultValue: saved });
        }
      }
    });
    return out;
  };
  const onRunNow = () => {
    const fields = askFields();
    if (!fields.length) { agentsData.runNow(id); return; }
    const values = {};
    fields.forEach(f => { values[f.key] = f.defaultValue || ""; });
    setAskRun({ fields, values });
  };
  const askRunReady = askRun && askRun.fields.every(f => String(askRun.values[f.key] || "").trim());
  const submitAskRun = () => {
    const vals = { ...askRun.values };
    setAskRun(null);
    agentsData.runNow(id, vals);
  };
  const setE = (k, v) => setEdit({ ...edit, [k]: v });
  const latest = runs && runs[0];

  // Open List Builder pre-filled from the seller profile's ICP; its "Save as ICP" links the search back here.
  const buildIcpSearch = async () => {
    // Departments resolve against List Builder's Job Function catalog (cached after the first load).
    await dataSearchState.loadDepartmentsCatalog();
    const catalog = { departments: dataSearchState.departmentsCatalog, legacyAliases: dataSearchState.departmentLegacyAliases };
    const { filters: lbFilters, selected } = icpToListBuilderImport(filters, catalog);
    const payload = { filters: lbFilters, selected, icpAgent: { id, name: agent.name } };
    history.push(`/list-builder?sgLb=${encodeListBuilderImport(payload)}`);
  };
  const icpSearchId = agent && agent.config && agent.config.icpSavedSearchId;
  const icpSearch = icpSearchId && (agentsData.savedSearches || []).find(s => s.id === icpSearchId);
  const setStatus = async status => { await agentsData.setStatus(id, status); };
  const saveSettings = async () => {
    if (!edit) return;
    // The name is only for display. When the channel list didn't load (Slack disconnected, or a
    // slow fetch) we can't resolve it — keep the stored one rather than wiping a good name.
    const slackChannel = (agentsData.slackChannels || []).find(c => c.id === edit.slackChannelId);
    const priorName = (agent.config || {}).slackChannelName;
    const cfg = {
      ...(agent.config || {}), notifyMode: edit.notifyMode,
      notifyChannels: edit.notifyMode === "OFF" ? [] : edit.notifyChannels,
      slackChannelId: edit.slackChannelId || null,
      slackChannelName: !edit.slackChannelId ? null : (slackChannel ? slackChannel.name : (priorName || null)),
      approvalMode: edit.requireApproval ? "REQUIRED" : "NONE", dailyRecordCap: Number(edit.dailyCap)
    };
    let trigger = agent.trigger;
    if (kind === "schedule") trigger = { type: "schedule.recurring", config: { ...((agent.trigger && agent.trigger.config) || {}), frequency: edit.frequency, time: edit.time, days: edit.frequency === "WEEKLY" ? edit.days : undefined, dayOfWeek: undefined } };
    else if (kind === "event") trigger = { type: "event." + edit.eventType, config: filterConfigFor(edit.eventType, edit.eventConfig) };
    // "Require approval" is enforced by the engine from config.approvalMode (it pauses before the first
    // outbound step every run). Drop any legacy approval.gate step — that step type no longer exists,
    // so a flow containing it would fail to run.
    const flow = (agent.flow || []).map(n => ({ ...n })).filter(n => n.stepType !== "approval.gate");
    await agentsData.updateAgent(id, { name: edit.name, config: cfg, trigger, flow });
    setSavedTick(true); setTimeout(() => setSavedTick(false), 2000);
  };

  return (
    <div className="ag-detail">
      <div className="ag-shell__head">
        <div>
          <BreadCrumbComponent breadCrumbValue={[
            { name: "Agents", link: "/agents" },
            { name: agent.name, link: `/agents/${agent.id}` }
          ]} />
        </div>
        <div className="ag-actions">
          <button className="ag-btn" onClick={() => history.push(`/agents/${id}/edit`)}>Edit flow</button>
          <button className="ag-btn" disabled={busy || testing} onClick={onTestRun}>{testing ? "Testing…" : "Test run (dry)"}</button>
          {agent.status === "ACTIVE" ? (
            <button className="ag-btn" onClick={() => setStatus("PAUSED")}>Pause</button>
          ) : (agent.status === "ARCHIVED" || agent.status === "DELETED") ? (
            <button className="ag-btn ag-btn--primary" onClick={() => setStatus("DRAFT")}>Restore</button>
          ) : (
            <button className="ag-btn ag-btn--primary" disabled={!approved} title={approved ? "" : "Approve the ICP first"} onClick={() => setStatus("ACTIVE")}>Turn on</button>
          )}
          <button className="ag-btn" disabled={busy || testing} onClick={onRunNow}>{busy ? "Running…" : "Run now"}</button>
        </div>
      </div>

      <div className="ag-detail__scroll">
        <div className="ag-detail__inner">

          {/* What this agent is for, in a sentence — written from its own flow, so it stays true
              after an edit. Missing on an agent whose summary hasn't been written yet. */}
          {!!agent.summary && <p className="ag-detail__summary">{agent.summary}</p>}

          {/* Overview strip */}
          <div className="ag-overview">
            <div className="ag-overview__facts">
              <div className="ag-ov">
                <span className="ag-ov__l">Status</span>
                <span className={`ag-pill ${agent.status === "ACTIVE" ? "ag-pill--live" : "ag-pill--draft"}`}>
                  {agent.status === "ACTIVE" && <span className="ag-dot" />}
                  {AGENT_STATUS_LABEL[agent.status] || "Draft"}
                </span>
              </div>
              <div className="ag-ov"><span className="ag-ov__l">Trigger</span><span className="ag-ov__v">{triggerSummary(agent)}</span></div>
              <div className="ag-ov"><span className="ag-ov__l">Notifications</span><span className="ag-ov__v">{notifySummary(edit, agent)}</span></div>
              {isIcp && <div className="ag-ov"><span className="ag-ov__l">ICP</span><span className="ag-ov__v">{approved ? "Approved" : "Needs approval"}</span></div>}
            </div>
            {latest && (
              <div className="ag-overview__stats">
                {/* A test run changes nothing — label its numbers as a projection, not results. */}
                <div className="ag-glance__stat"><div className={`v${latest.dryRun ? "" : " v--good"}`}>{latest.enrolledCount}</div><div className="l">{latest.dryRun ? (enrolls ? "Would enroll" : "Would process") : doneLabel}</div></div>
                <div className="ag-glance__stat"><div className="v">{latest.skippedCount}</div><div className="l">{latest.dryRun ? "Would skip" : "Skipped"}</div></div>
                <div className="ag-glance__stat"><div className="v">{latest.creditsUsed}</div><div className="l">{latest.dryRun ? "Est. credits" : "Credits"}</div></div>
              </div>
            )}
          </div>

          {/* A paused run can only continue from the Approvals queue — point there. */}

          {runs && runs.some(r => r.status === "WAITING_APPROVAL") && (

            <div className="ag-awaitbar">

              <i className="material-icons-outlined">hourglass_empty</i>

              <span>A run is paused, waiting for your review before the agent acts.</span>

              <button className="ag-btn ag-btn--primary ag-btn--sm" onClick={() => history.push("/agents/approvals")}>Review</button>

            </div>

          )}

          {/* Recent runs — the primary content */}
          <div className="ag-panel ag-panel--flush">
            <div className="ag-panel__head">
              <h2 className="ag-panel__title">Recent runs</h2>
              <button className="ag-link" onClick={() => agentsData.fetchRuns(id)}>Refresh</button>
            </div>
            {(!runs || runs.length === 0) && <div className="ag-hint">No runs yet. Use “Run now” or “Test run” to see activity here.</div>}
            {runs && runs.length > 0 && (
              <table className="ag-table">
                <thead>
                  <tr><th style={{ width: 24 }} /><th>When</th><th>Trigger</th><th>Status</th><th>{doneLabel}</th><th>Skipped</th><th>Credits</th></tr>
                </thead>
                <tbody>
                  {runs.map(r => {
                    const open = expandedRun === r.id;
                    const records = agentsData.runRecords[r.id];
                    const loadingR = agentsData.runRecordsLoading[r.id];
                    return (
                      <React.Fragment key={r.id}>
                        <tr className="ag-row--click" onClick={() => toggleRun(r.id)}>
                          <td className="ag-caret">{open ? "▾" : "▸"}</td>
                          <td>{r.startedAt ? new Date(r.startedAt).toLocaleString() : "-"}</td>
                          <td>
                            {friendlyTrigger(r.triggerSource)}{r.dryRun ? " · test" : ""}
                            {/* What actually happened to start this run, e.g. "Stage changed: Nurturing → Interested". */}
                            {r.triggerNote && <span className="ag-run__why" title={r.triggerNote}>{r.triggerNote}</span>}
                          </td>
                          <td><span className={`ag-pill ${runPill(r.status)}`}>{STATUS_LABEL[r.status] || prettify(r.status)}</span></td>
                          <td>{r.enrolledCount}</td>
                          <td>{r.skippedCount}</td>
                          <td>{r.creditsUsed}</td>
                        </tr>
                        {open && (
                          <tr className="ag-row--detail">
                            <td colSpan={7}>
                              {(r.steps || []).length > 0 && (
                                <div className="ag-steps">
                                  <div className="ag-steps__title">{r.dryRun ? "Plan — nothing was changed" : "Steps"}</div>
                                  {r.steps.map((s, i) => (
                                    <div className="ag-steps__row" key={`${s.nodeId}-${i}`}>
                                      <span className="ag-steps__name">{stepName(s.stepType)}</span>
                                      <span className="ag-steps__count">{s.entered} → {s.processed}</span>
                                      <span className="ag-steps__note">{s.note || "—"}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {loadingR && <div className="ag-hint">Loading contacts…</div>}
                              {!loadingR && r.dryRun && (!records || records.length === 0) && (
                                <div className="ag-hint">Dry runs don't record per-contact outcomes.</div>
                              )}
                              {!loadingR && !r.dryRun && (!records || records.length === 0) && (
                                <div className="ag-hint">No contact-level detail for this run.</div>
                              )}
                              {!loadingR && records && records.length > 0 && (
                                <table className="ag-subtable">
                                  <thead>
                                    <tr><th>Contact</th><th>Email</th><th>Company</th><th>Outcome</th><th>Detail</th></tr>
                                  </thead>
                                  <tbody>
                                    {records.map(rec => {
                                      const d = DISPOSITION[rec.disposition] || { label: prettify(rec.disposition), cls: "ag-disp--neutral" };
                                      const trail = (rec.trace || []).join(" → ");
                                      return (
                                        <tr key={rec.id || rec.contactId}>
                                          <td>{rec.name || "—"}</td>
                                          <td>{rec.email || <span className="ag-muted">no email</span>}</td>
                                          <td>{rec.company || "—"}</td>
                                          <td><span className={`ag-disp ${d.cls}`}>{d.label}</span></td>
                                          <td className="ag-detail-cell">{rec.reason || trail || "—"}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* The agent's steps in order — so you can see what it does without opening the editor */}
          <div className="ag-panel">
            <div className="ag-panel__head">
              <h2 className="ag-panel__title">Steps</h2>
              <button className="ag-link" onClick={() => history.push(`/agents/${id}/edit`)}>Edit flow</button>
            </div>
            {(agent.flow || []).filter(n => n.stepType !== "notify.digest").length === 0
              ? <div className="ag-hint">No steps yet.</div>
              : (
                <ol className="ag-flowlist">
                  {(agent.flow || []).filter(n => n.stepType !== "notify.digest").map((n, i) => (
                    <li key={n.nodeId || i} className="ag-flowlist__item">
                      <span className="ag-flowlist__kind">{stepKindLabel(n.stepType)}</span>
                      <span className="ag-flowlist__name">{stepName(n.stepType)}</span>
                    </li>
                  ))}
                </ol>
              )}
          </div>

          {/* ICP — only for ICP-based agents */}
          {isIcp && (
            <div className="ag-panel">
              <div className="ag-panel__head">
                <h2 className="ag-panel__title">Ideal customer profile</h2>
                <span className={`ag-pill ${approved ? "ag-pill--live" : "ag-pill--draft"}`}>{approved ? "Approved" : "Needs approval"}</span>
              </div>
              {icpSearchId ? (
                <p className="ag-hint">
                  The ICP is the List Builder saved search <b>{icpSearch ? icpSearch.label : "(saved search)"}</b> — every run finds new people with it.
                </p>
              ) : (
                <p className="ag-hint">
                  Build the ICP as a List Builder search, pre-filled from the seller profile. Refine the filters, check the results, then
                  save it as this agent's ICP — the agent won't run until you do.
                </p>
              )}
              <div className="ag-form__actions" style={{ flexWrap: "wrap", gap: 10 }}>
                <button className="ag-btn ag-btn--primary" disabled={busy} onClick={buildIcpSearch}>
                  {icpSearchId ? "Rebuild from seller profile in List Builder →" : "Build ICP search in List Builder →"}
                </button>
              </div>
              <label className="ag-field" style={{ marginTop: 12, maxWidth: 420 }}>
                <span className="ag-field__label">{icpSearchId ? "Use a different saved search" : "Or use a saved search you already have"}</span>
                <AgentSelect
                  searchable
                  placeholder="Select a saved search…"
                  value={icpSearchId || ""}
                  onChange={v => v && v !== icpSearchId && agentsData.setIcpSavedSearch(id, v)}
                  options={(agentsData.savedSearches || []).map(ss => ({ value: ss.id, label: ss.label }))}
                />
              </label>
            </div>
          )}

          {/* Settings — collapsible (editing is secondary) */}
          {edit && (
            <div className="ag-panel">
              <button className="ag-panel__toggle" onClick={() => setSettingsOpen(o => !o)}>
                <span className="ag-panel__title">Settings</span>
                <span className="ag-panel__toggleright">
                  {savedTick && <span className="ag-pill ag-pill--live"><span className="ag-dot" />Saved</span>}
                  <span className="ag-panel__chev">{settingsOpen ? "▾" : "▸"}</span>
                </span>
              </button>
              {settingsOpen && (
                <>
                  <div className="ag-icp" style={{ marginTop: 16 }}>
                    <label className="ag-field">
                      <span className="ag-field__label">Agent name</span>
                      <input className="ag-input" value={edit.name} onChange={e => setE("name", e.target.value)} />
                    </label>
                    {kind === "schedule" && (
                      <>
                        <label className="ag-field">
                          <span className="ag-field__label">Frequency</span>
                          <AgentSelect value={edit.frequency} onChange={v => setE("frequency", v)} options={FREQUENCY_OPTIONS} />
                        </label>
                        {edit.frequency === "WEEKLY" && <DayOfWeekField label="Days" value={edit.days} onChange={v => setE("days", v)} />}
                        <label className="ag-field">
                          <span className="ag-field__label">Run time</span>
                          <input className="ag-input" type="time" value={edit.time} onChange={e => setE("time", e.target.value)} />
                        </label>
                      </>
                    )}
                    {kind === "event" && (
                      <>
                        <label className="ag-field">
                          <span className="ag-field__label">Trigger event</span>
                          <AgentSelect value={edit.eventType} searchable options={eventOptions()}
                            onChange={v => { setE("eventType", v); setE("eventConfig", {}); }} />
                        </label>
                        <TriggerFilters eventType={edit.eventType} config={edit.eventConfig}
                          onChange={v => setE("eventConfig", v)} />
                      </>
                    )}
                    {isIcp && (
                      <label className="ag-field">
                        <span className="ag-field__label">Contacts / run</span>
                        <input className="ag-input" type="number" min="1" value={edit.dailyCap} onChange={e => setE("dailyCap", e.target.value)} />
                      </label>
                    )}
                    <label className="ag-field">
                      <span className="ag-field__label">Notify me</span>
                      <AgentSelect value={edit.notifyMode} onChange={v => setE("notifyMode", v)} options={NOTIFY_OPTIONS} />
                    </label>
                    {edit.notifyMode !== "OFF" && (
                      <label className="ag-field">
                        <span className="ag-field__label">Send it to</span>
                        <div className="ag-chanpick">
                          {["email", "slack"].map(ch => {
                            const on = edit.notifyChannels.indexOf(ch) !== -1;
                            const noSlack = ch === "slack" && agentsData.slackChannelsLoaded
                              && !(agentsData.slackChannels || []).length;
                            return (
                              <label key={ch} className={"ag-chanpick__opt" + (noSlack ? " ag-chanpick__opt--off" : "")}>
                                <input type="checkbox" checked={on} disabled={noSlack}
                                  onChange={e => {
                                    const next = edit.notifyChannels.filter(c => c !== ch);
                                    if (e.target.checked) next.push(ch);
                                    // Never leave it with nowhere to go — that's what "Off" is for.
                                    setE("notifyChannels", next.length ? next : [ch === "email" ? "slack" : "email"]);
                                  }} />
                                <span>{CHANNEL_LABEL[ch]}</span>
                              </label>
                            );
                          })}
                        </div>
                        {agentsData.slackChannelsLoaded && !(agentsData.slackChannels || []).length && (
                          <span className="ag-hint">Connect Slack in Settings to send these there too.</span>
                        )}
                      </label>
                    )}
                    {edit.notifyMode !== "OFF" && edit.notifyChannels.indexOf("slack") !== -1
                      && !!(agentsData.slackChannels || []).length && (
                      <label className="ag-field">
                        <span className="ag-field__label">Slack channel</span>
                        <AgentSelect value={edit.slackChannelId} searchable
                          onChange={v => setE("slackChannelId", v)}
                          options={[{ value: "", label: "Your team's channel" }].concat(
                            (agentsData.slackChannels || []).map(c => ({
                              value: c.id, label: "#" + c.name + (c.teamDefault ? " (team's channel)" : "")
                            })))} />
                        <span className="ag-hint">This agent posts here instead of the team's channel.</span>
                      </label>
                    )}
                  </div>
                  <label className="ag-toggle-field" style={{ marginTop: 4 }}>
                    <input type="checkbox" checked={edit.requireApproval} onChange={e => setE("requireApproval", e.target.checked)} />
                    <span>
                      <b>Require my approval before acting</b>
                      <span className="ag-hint" style={{ marginTop: 2 }}>Every run pauses for your review before the agent acts.</span>
                    </span>
                  </label>
                  <div className="ag-form__actions">
                    <button className="ag-btn ag-btn--primary" disabled={busy || !edit.name.trim()} onClick={saveSettings}>
                      {busy ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {askRun && (
        <div className="af-picker__overlay" onMouseDown={() => setAskRun(null)}>
          <div className="af-picker" onMouseDown={e => e.stopPropagation()}>
            <div className="af-picker__head">
              <h3>Run agent</h3>
              <button className="ag-seqm__close" onClick={() => setAskRun(null)}>×</button>
            </div>
            <div className="af-picker__ctx">Enter the inputs for this run.</div>
            <div className="af-picker__body">
              {askRun.fields.map(f => (
                <label key={f.key} className="ag-field" style={{ margin: "4px 10px 12px" }}>
                  <span className="ag-field__label">{f.label}</span>
                  {f.type === "select" && f.options ? (
                    <AgentSelect value={askRun.values[f.key] || ""}
                      onChange={v => setAskRun(a => ({ ...a, values: { ...a.values, [f.key]: v } }))}
                      options={(f.options || []).map(o => ({ value: o.value, label: o.label }))} />
                  ) : (
                    <input className="ag-input" value={askRun.values[f.key] || ""} placeholder="Enter a value…"
                      onChange={e => {
                        // Read the value now: React 16 recycles the event before a functional updater runs.
                        const v = e.target.value;
                        setAskRun(a => ({ ...a, values: { ...a.values, [f.key]: v } }));
                      }} />
                  )}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 14px 16px" }}>
              <button className="ag-btn" onClick={() => setAskRun(null)}>Cancel</button>
              <button className="ag-btn ag-btn--primary" disabled={!askRunReady} onClick={submitAskRun}>Run →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default AgentDetail;
